import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Paper,
  Collapse,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  Person,
  Email,
  AdminPanelSettings,
  ManageAccounts,
  Schedule as ScheduleIcon,
  Engineering,
  Visibility,
  SupervisorAccount,
  CheckCircle,
  Block,
  PersonAdd,
  Login,
} from '@mui/icons-material';

import { UserRole } from "../../types";
import type { SearchFilters, SortOptions, UserStats } from "../../types";

interface UserFiltersProps {
  filters: SearchFilters;
  sortOptions: SortOptions;
  onFiltersChange: (filters: SearchFilters) => void;
  onSortChange: (sort: SortOptions) => void;
  onClearFilters: () => void;
  totalItems?: number;
  stats?: UserStats;
}

const UserFilters: React.FC<UserFiltersProps> = ({
  filters,
  sortOptions,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  totalItems = 0,
  stats,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      search: event.target.value,
    });
  };

  const handleRoleChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      userRoles: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleActiveOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      activeUsersOnly: event.target.checked,
      inactiveUsersOnly: event.target.checked ? false : filters.inactiveUsersOnly,
    });
  };

  const handleInactiveOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      inactiveUsersOnly: event.target.checked,
      activeUsersOnly: event.target.checked ? false : filters.activeUsersOnly,
    });
  };

  const handleOnlineOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      onlineUsersOnly: event.target.checked,
    });
  };

  const handleDateFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      dateFrom: event.target.value,
    });
  };

  const handleDateToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      dateTo: event.target.value,
    });
  };

  const handleSortFieldChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      field: event.target.value,
    });
  };

  const handleSortDirectionChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      direction: event.target.value,
    });
  };

  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (filters.search) count++;
    if (filters.userRoles && filters.userRoles.length > 0) count++;
    if (filters.activeUsersOnly) count++;
    if (filters.inactiveUsersOnly) count++;
    if (filters.onlineUsersOnly) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    return count;
  };

  const getRoleIcon = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return <AdminPanelSettings fontSize="small" />;
      case UserRole.MANAGER:
        return <ManageAccounts fontSize="small" />;
      case UserRole.SCHEDULER:
        return <ScheduleIcon fontSize="small" />;
      case UserRole.ENGINEER:
        return <Engineering fontSize="small" />;
      case UserRole.DIRECTOR:
        return <SupervisorAccount fontSize="small" />;
      case UserRole.VIEWER:
        return <Visibility fontSize="small" />;
      default:
        return <Person fontSize="small" />;
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'error';
      case UserRole.MANAGER:
        return 'warning';
      case UserRole.SCHEDULER:
        return 'info';
      case UserRole.ENGINEER:
        return 'success';
      case UserRole.DIRECTOR:
        return 'secondary';
      case UserRole.VIEWER:
        return 'default';
      default:
        return 'default';
    }
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      {/* Основная строка фильтров */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: expanded ? 2 : 0 }}>
        {/* Поиск */}
        <TextField
          placeholder={t('users.searchPlaceholder')}
          value={filters.search || ''}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filters.search && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onFiltersChange({ ...filters, search: '' })}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Роли */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('users.roles')}</InputLabel>
          <Select
            multiple
            value={filters.userRoles || []}
            onChange={handleRoleChange}
            label={t('users.roles')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip
                    key={value}
                    label={t(`users.userRoles.${value.toLowerCase()}`)}
                    size="small"
                    icon={getRoleIcon(value as UserRole)}
                    color={getRoleColor(value as UserRole)}
                  />
                ))}
              </Box>
            )}
          >
            {Object.values(UserRole).map((role) => (
              <MenuItem key={role} value={role}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getRoleIcon(role)}
                  {t(`users.userRoles.${role.toLowerCase()}`)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Сортировка */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('common.sortBy')}</InputLabel>
          <Select
            value={sortOptions.field || 'full_name'}
            onChange={handleSortFieldChange}
            label={t('common.sortBy')}
          >
            <MenuItem value="full_name">{t('users.fullName')}</MenuItem>
            <MenuItem value="username">{t('users.username')}</MenuItem>
            <MenuItem value="email">{t('users.email')}</MenuItem>
            <MenuItem value="role">{t('users.role')}</MenuItem>
            <MenuItem value="last_login">{t('users.lastLogin')}</MenuItem>
            <MenuItem value="created_at">{t('common.dateCreated')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>{t('common.order')}</InputLabel>
          <Select
            value={sortOptions.direction || 'asc'}
            onChange={handleSortDirectionChange}
            label={t('common.order')}
          >
            <MenuItem value="asc">{t('common.ascending')}</MenuItem>
            <MenuItem value="desc">{t('common.descending')}</MenuItem>
          </Select>
        </FormControl>

        {/* Кнопки управления */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterList />}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setExpanded(!expanded)}
            color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
          >
            {t('common.filters')}
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Button>

          {activeFiltersCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={onClearFilters}
              color="error"
            >
              {t('common.clear')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Расширенные фильтры */}
      <Collapse in={expanded}>
        <Grid container spacing={2}>
          {/* Быстрые фильтры */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              {t('users.quickFilters')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.activeUsersOnly || false}
                    onChange={handleActiveOnlyChange}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircle fontSize="small" color="success" />
                    {t('users.activeOnly')}
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.inactiveUsersOnly || false}
                    onChange={handleInactiveOnlyChange}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Block fontSize="small" color="error" />
                    {t('users.inactiveOnly')}
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.onlineUsersOnly || false}
                    onChange={handleOnlineOnlyChange}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Login fontSize="small" color="info" />
                    {t('users.onlineOnly')}
                  </Box>
                }
              />
            </Box>
          </Grid>

          {/* Фильтры по дате */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              {t('users.registrationPeriod')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label={t('common.dateFrom')}
                type="date"
                size="small"
                value={filters.dateFrom || ''}
                onChange={handleDateFromChange}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonAdd fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              <TextField
                label={t('common.dateTo')}
                type="date"
                size="small"
                value={filters.dateTo || ''}
                onChange={handleDateToChange}
                InputLabelProps={{ shrink: true }}
                sx={{ flexGrow: 1 }}
              />
            </Box>
          </Grid>

          {/* Статистика */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              {t('users.statistics')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {t('users.totalUsers')}: {totalItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('users.activeUsers')}: {stats?.active_users ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('users.inactiveUsers')}: {stats?.inactive_users ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('users.onlineUsers')}: {stats?.online_users ?? 0}
              </Typography>
            </Box>
          </Grid>

          {/* Быстрые действия */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('users.quickActions')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                startIcon={<AdminPanelSettings />}
                onClick={() => onFiltersChange({ ...filters, userRoles: [UserRole.ADMIN] })}
              >
                {t('users.showAdmins')}
              </Button>
              <Button
                size="small"
                startIcon={<ManageAccounts />}
                onClick={() => onFiltersChange({ ...filters, userRoles: [UserRole.MANAGER] })}
              >
                {t('users.showManagers')}
              </Button>
              <Button
                size="small"
                startIcon={<CheckCircle />}
                onClick={() => onFiltersChange({ ...filters, activeUsersOnly: true })}
              >
                {t('users.showActiveUsers')}
              </Button>
              <Button
                size="small"
                startIcon={<Login />}
                onClick={() => onFiltersChange({ ...filters, onlineUsersOnly: true })}
              >
                {t('users.showOnlineUsers')}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Информация о результатах */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.showingResults', { total: totalItems })}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default UserFilters;
